import { AgentInteractionStatus } from "@/common/constants/callSession";
import { SkillVariableModel } from "./skills";
import { MonitorTypes } from "@/repository/models/monitoring";
import { PlatformType } from "../constants/shared";

export class CallSession {
	queueId?: string;
	queueName?: string;
	skills?: SkillVariableModel[] = [];
	startDate?: string;
	firstAgentAssignedAt?: string;
	id?: string; //scenarioId
	caller?: CallerModel;
	state?: AgentInteractionStatus;
	currentAgent?: CallSessionAgentModel;
	transferTarget?: TransferTarget;
	hasCallerLeft?: boolean;
	wasCallTransferred?: boolean;
	callType?: CallType;
	internalMediaIdentificationName?: string;
	supervisorId?: string;
	isAcceptedCall?: boolean;
	monitorType?: MonitorTypes;
	supervisingStartDate?: string;
	hasBeenHandled?: boolean;
	isFallback?: boolean;
	isColdTransferLeave?: boolean;
	isColdTransferRejected?: boolean;
	isCallbackRegistered?: boolean;
	isIVRWarmTransfer?: boolean;
	secondaryAgentName?: string;
	isSimultaneousOffering?: boolean;
	simultaneousOfferingAgentIds?: string[];
	isMsTeamsToasterOffering?: boolean;
	activeVariableText?: string;
	activeVariableTextIsAiGenerated?: boolean;
	offeringVariableText?: string;
	offeringVariableTextIsAiGenerated?: boolean;
	transferInitiatorName?: string;
	callId?: string;
	wasWarmTransferConferenceMode?: boolean;
	platformType?: PlatformType;
	transcriptionStarted?: boolean;
	transcriptionInProgress?: boolean;
	transcriptionLanguage?: string;
	recordingInProgress?: boolean;
	activeWrapUpAgentsId?: string[];
	fallbackType?: FallbackType;
	fallbackFrom?: string;
	callbackQueueName?: string;
	isCallParked?: boolean;
	isCallIgnored?: boolean;
	isAgentEnableWrapupCheckDone?: boolean;
	secondaryAgentId?: string;
	lastWarmCancelDate?: string;
	isCallerClosedBeforeAnswer?: boolean;
	/**
	 * Webchat conversation (messaging session) id when this call is an agent-initiated
	 * video call started from an ongoing webchat interaction; undefined otherwise.
	 */
	webchatConversationId?: string;
	crmCallerContext?: CrmCallerContext;
}

/**
 * CRM that produced a match. Serialized by name from the backend `CrmProvider` enum, so the
 * wire values are the literal strings below. Optional on the wire: contexts persisted before
 * multi-CRM support omit it, and the picker treats a missing value as Salesforce.
 */
export enum CrmProvider {
	Salesforce = "Salesforce",
	Dynamics = "Dynamics",
	HubSpot = "HubSpot",
}

export interface CrmCallerContext {
	contactId?: string;
	contactName?: string;
	accountId?: string;
	accountName?: string;
	/** Click-through URL to the matched record in Salesforce Lightning. Null when the tenant's instance URL isn't configured. */
	contactViewUrl?: string;
	/** Click-through URL to the matched record's Account in Salesforce Lightning. */
	accountViewUrl?: string;
	openCaseCount?: number;
	cases?: CrmMatchedCase[];
	selectedCaseIds?: string[];
	/**
	 * Salesforce object the related records live on (Case, Event, Task, WorkOrder, custom).
	 * Stamped at lookup time from the winning configuration. Used by the picker to disambiguate
	 * non-Case related records that lack Case-specific fields (CaseNumber/Priority/Status).
	 */
	caseTargetObject?: string;
	customAttributes?: Record<string, string>;
	matchType?: "NoMatch" | "Exact" | "Multiple";
	lookupTimestamp?: string;
	lookupConfigurationName?: string;
	/** CRM that produced the most recent match (mirrors each entry's {@link CrmMatch.provider}). */
	provider?: CrmProvider;
	/**
	 * One entry per Salesforce Lookup step the IVR/messaging flow ran for this interaction.
	 * Successive steps append unless the step's Overwrite flag is set (which clears the list
	 * before adding). The agent picker renders each entry as a collapsible row. Old call
	 * sessions may have this list empty even though the top-level fields are populated — the
	 * picker tolerates that by synthesising a single match from the scalar fields.
	 */
	matches?: CrmMatch[];
}

export interface CrmMatch {
	lookupConfigurationId?: string;
	lookupConfigurationName?: string;
	lookupObject?: string;
	contactId?: string;
	contactName?: string;
	contactViewUrl?: string;
	accountId?: string;
	accountName?: string;
	accountViewUrl?: string;
	openCaseCount?: number;
	cases?: CrmMatchedCase[];
	caseTargetObject?: string;
	customAttributes?: Record<string, string>;
	matchType?: "NoMatch" | "Exact" | "Multiple";
	lookupTimestamp?: string;
	/** CRM that produced this match. Drives the per-card provider chip in the agent panel. */
	provider?: CrmProvider;
}

export interface CrmMatchedCase {
	id: string;
	caseNumber?: string;
	priority?: string;
	status?: string;
	subject?: string;
	/** Click-through URL to this record in Salesforce Lightning. Null when the tenant's instance URL isn't configured. */
	viewUrl?: string;
}

export enum FallbackType {
	FromAgent = 0,
	FromQueue = 1,
}

export interface CallerModel {
	id: string;
	name: string;
	contactInformation: string;
}

export interface TransferTarget {
	name: string;
}

export interface CallSessionAgentModel {
	displayName: string;
	id: string;
	sip: string;
	type: CallSessionParticipantType;
}

export enum CallSessionParticipantType {
	Caller,
	Bot,
	PrimaryAgent,
	SecondaryAgent,
	Supervisor,
	Observer,
}

export enum CallType {
	Incoming,
	Outgoing,
}
